import { Materials, countOf, iconOf, labelOf } from '../game/buildings/Materials';

/**
 * Co gracz ma przy sobie.
 *
 * A line in the top bar: an icon and a number for each material held, and
 * nothing at all for the ones that are not. A store with three things in it out
 * of a dictionary of thirty should read as three things, not as twenty-seven
 * zeroes with three exceptions hidden among them.
 *
 * Clicking it opens the full reading — the same materials, with their names
 * spelled out. Collapsed is for glancing at while walking; open is for
 * checking. Nothing is in the open list that is not in the summary: it is the
 * same set, said at more length.
 *
 * HTML over the canvas, like everything else that has words in it: the game
 * renders into a small buffer scaled up by a whole number, and text in there is
 * a smear.
 */

export class PurseBar {
  private readonly root = document.createElement('div');
  private readonly summary = document.createElement('button');
  private readonly detail = document.createElement('div');

  private items: { material: string; amount: number }[] = [];
  private open = false;

  constructor(
    container: HTMLElement,
    private readonly materials: Materials
  ) {
    this.root.className = 'purse';
    this.summary.type = 'button';
    this.summary.className = 'purse-summary';
    this.detail.className = 'purse-detail';
    this.detail.hidden = true;

    this.summary.addEventListener('click', () => {
      this.open = !this.open;
      this.render();
    });

    this.root.append(this.summary, this.detail);
    container.appendChild(this.root);
    this.render();
  }

  /**
   * What the server says is in there.
   *
   * Zeroes are dropped here rather than at the sender: a store empties by an
   * amount going to zero, and a row that stays at zero is how the server knows
   * the material was ever there. What a player wants to see is what they have.
   */
  set(items: { material: string; amount: number }[]): void {
    this.items = items.filter((item) => item.amount > 0);
    this.render();
  }

  dispose(): void {
    this.root.remove();
  }

  private render(): void {
    if (this.items.length === 0) {
      this.summary.textContent = 'magazyn pusty';
      this.summary.disabled = true;
      this.detail.hidden = true;
      return;
    }

    this.summary.disabled = false;
    this.summary.textContent = this.items
      .map((item) => `${iconOf(this.materials, item.material)} ${item.amount}`)
      .join('  ');

    this.detail.hidden = !this.open;
    if (!this.open) return;

    this.detail.innerHTML = this.items
      .map(
        (item) =>
          `<span class="purse-line">
             <span class="purse-icon">${iconOf(this.materials, item.material)}</span>
             <span class="purse-name">${labelOf(this.materials, item.material)}</span>
             <strong>${countOf(this.materials, item.material, item.amount)}</strong>
           </span>`
      )
      .join('');
  }
}
